import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import { useEffect, useMemo, useState } from 'react';

import { Box } from '#/components/Box/Box';
import { FieldErrorContainer } from '#/components/Form/Field/FieldErrorContainer';
import { FieldLabel } from '#/components/Form/Field/FieldLabel';
import { useFormContext } from '#/components/Form/FormContext';
import { withIdealistStripeProvider } from '#/components/Form/Stripe/IdealistStripeProvider';
import type { StripeCreditCardAdditionalData } from '#/components/Form/Stripe/StripeCreditCardAdditionalData';
import {
  StripeElementContainer,
  stripeElementStyles,
} from '#/components/Form/Stripe/StripeElementContainer';
import { useStripeFieldStatus } from '#/components/Form/Stripe/useStripeFieldStatus';
import { useStripeTokenFormInterceptor } from '#/components/Form/Stripe/useStripeTokenFormInterceptor';
import { AnimatedCheckmark } from '#/components/Icon/AnimatedCheckmark';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors } from '#/theme/theme';

import { CreditCardCvcTooltip } from './CreditCardCvcTooltip';

type Props = {
  wrap?: boolean;
  disabled?: boolean;
  getAdditionalStripeData: () => StripeCreditCardAdditionalData;
  onTokenCreated?: (stripeTokenId: string) => void;
  qaId?: string;
};

function UnwrappedCreditCardField({
  wrap,
  disabled,
  getAdditionalStripeData,
  onTokenCreated,
  qaId,
}: Props) {
  const { i18n } = useI18n();

  const [cardNumberStatus, onChangeCardNumber] = useStripeFieldStatus();
  const [cardExpiryStatus, onChangeCardExpiryStatus] = useStripeFieldStatus();
  const [cardCvcStatus, onChangeCardCvcStatus] = useStripeFieldStatus();
  const allComplete = useMemo(
    () =>
      cardNumberStatus.type === 'complete' &&
      cardExpiryStatus.type === 'complete' &&
      cardCvcStatus.type === 'complete',
    [cardNumberStatus, cardExpiryStatus, cardCvcStatus],
  );

  const [cardBrand, setCardBrand] = useState<string>();
  const { updateSubmitDisabled, fieldQaIdPrefix } = useFormContext();
  const qaIdPrefix = qaId ?? fieldQaIdPrefix;
  const { stripeTokenError } = useStripeTokenFormInterceptor({
    getAdditionalStripeData,
    onTokenCreated,
  });

  useEffect(() => {
    updateSubmitDisabled(!allComplete);

    return () => {
      updateSubmitDisabled(false);
    };
  }, [cardExpiryStatus, allComplete, updateSubmitDisabled]);

  return (
    <Box m="-8px" mb={22}>
      <Box display="flex" flexWrap="wrap" qaId={`${qaIdPrefix}-credit-card`}>
        <Box position="relative" width={wrap ? 1 : [1, null, 1 / 2]} p="8px">
          <FieldLabel htmlFor={`${qaIdPrefix}-number`}>
            {i18n('Card Number')}
          </FieldLabel>

          <StripeElementContainer>
            <CardNumberElement
              id={`${qaIdPrefix}-number`}
              options={{ showIcon: true, style: stripeElementStyles }}
              onChange={(event) => {
                setCardBrand(event.brand);
                onChangeCardNumber(event);
              }}
            />
          </StripeElementContainer>

          {cardNumberStatus.type === 'error' && (
            <FieldErrorContainer>
              {cardNumberStatus.errorMessage}
            </FieldErrorContainer>
          )}
        </Box>

        <Box
          position="relative"
          width={wrap ? [1, null, 1 / 2] : [1 / 2, null, 1 / 4]}
          p="8px"
        >
          <FieldLabel htmlFor={`${qaIdPrefix}-expiry-date`}>
            {i18n('Expiry Date')}
          </FieldLabel>

          <StripeElementContainer>
            <CardExpiryElement
              id={`${qaIdPrefix}-expiry-date`}
              options={{ style: stripeElementStyles }}
              onChange={onChangeCardExpiryStatus}
            />
          </StripeElementContainer>

          {cardExpiryStatus.type === 'error' && (
            <FieldErrorContainer>
              {cardExpiryStatus.errorMessage}
            </FieldErrorContainer>
          )}
        </Box>

        <Box
          position="relative"
          width={wrap ? [1, null, 1 / 2] : [1 / 2, null, 1 / 4]}
          p="8px"
        >
          <FieldLabel htmlFor={`${qaIdPrefix}-cvc`}>
            <Box display="flex" alignItems="center">
              {i18n('CVC')}
              <Box ml="4px" verticalAlign="middle">
                <LegacyTooltip disabled={disabled} tabIndex={-1}>
                  <Box textAlign="center">
                    <CreditCardCvcTooltip brand={cardBrand} />
                  </Box>
                </LegacyTooltip>
              </Box>
            </Box>
          </FieldLabel>

          <StripeElementContainer>
            <CardCvcElement
              id={`${qaIdPrefix}-cvc`}
              options={{ style: stripeElementStyles }}
              onChange={onChangeCardCvcStatus}
            />
            {allComplete && (
              <Box
                display={['none', null, 'flex']}
                position="absolute"
                right={-20}
                top="50%"
                width={24}
                height={24}
                alignItems="center"
                justifyContent="center"
                borderRadius="50%"
                bg={colors.brandGreen}
              >
                <AnimatedCheckmark size={16} colorName="white" />
              </Box>
            )}
          </StripeElementContainer>

          {cardCvcStatus.type === 'error' && (
            <FieldErrorContainer>
              {cardCvcStatus.errorMessage}
            </FieldErrorContainer>
          )}
        </Box>
      </Box>

      {stripeTokenError && (
        <FieldErrorContainer>{stripeTokenError}</FieldErrorContainer>
      )}
    </Box>
  );
}

export const CreditCardField = withIdealistStripeProvider(
  UnwrappedCreditCardField,
);
